/**
 * Error Messages Utility
 *
 * This module provides error messages for the application layer.
 * Since application layer files are plain TypeScript (non-React),
 * we export the error messages as constants.
 *
 * Note: These messages default to English. In the future, we could
 * enhance this to support dynamic locale switching by accepting
 * locale as a parameter from the React layer.
 */

// Define the supported locales type matching the intlayer config
type SupportedLocales = 'nl' | 'en' | 'fr' | 'de'

// Type for error message functions
type ErrorMessageFunction = (locale?: SupportedLocales) => string
type ErrorMessageWithParamFunction = (param: string, locale?: SupportedLocales) => string

// Error message definitions matching the content file
const errorMessageDefinitions = {
  // Element-related errors
  elementNotFound: ((param: string, locale: SupportedLocales = 'en') => {
    const messages = {
      nl: `Element niet gevonden: ${param}`,
      en: `Element not found: ${param}`,
      de: `Element nicht gefunden: ${param}`,
      fr: `Élément introuvable : ${param}`,
    }
    return messages[locale] || messages.en
  }) as ErrorMessageWithParamFunction,

  failedToRemoveElement: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Verwijderen van element mislukt',
      en: 'Failed to remove element',
      de: 'Element konnte nicht entfernt werden',
      fr: "Échec de la suppression de l'élément",
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  failedToUpdateElement: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Bijwerken van element mislukt',
      en: 'Failed to update element',
      de: 'Element konnte nicht aktualisiert werden',
      fr: "Échec de la mise à jour de l'élément",
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  failedToAddElement: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Toevoegen van element mislukt',
      en: 'Failed to add element',
      de: 'Element konnte nicht hinzugefügt werden',
      fr: "Échec de l'ajout de l'élément",
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  invalidPosition: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Ongeldige positie',
      en: 'Invalid position',
      de: 'Ungültige Position',
      fr: 'Position invalide',
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  invalidDimensions: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Ongeldige afmetingen',
      en: 'Invalid dimensions',
      de: 'Ungültige Abmessungen',
      fr: 'Dimensions invalides',
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  // Image upload errors
  failedToGetUploadUrl: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Ophalen van upload URL mislukt',
      en: 'Failed to get upload URL',
      de: 'Upload-URL konnte nicht abgerufen werden',
      fr: "Échec de l'obtention de l'URL de téléchargement",
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  failedToUploadImage: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Uploaden van afbeelding mislukt',
      en: 'Failed to upload image',
      de: 'Bild konnte nicht hochgeladen werden',
      fr: "Échec du téléchargement de l'image",
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  // Design-related errors
  failedToCreateDesign: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Aanmaken van ontwerp mislukt',
      en: 'Failed to create design',
      de: 'Design konnte nicht erstellt werden',
      fr: 'Échec de la création du design',
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  failedToLoadDesign: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Laden van ontwerp mislukt',
      en: 'Failed to load design',
      de: 'Design konnte nicht geladen werden',
      fr: 'Échec du chargement du design',
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,

  retryingSaveAttempt: ((attemptNumber: string, locale: SupportedLocales = 'en') => {
    const messages = {
      nl: `Poging opnieuw opslaan ${attemptNumber}`,
      en: `Retrying save attempt ${attemptNumber}`,
      de: `Speicherversuch wird wiederholt ${attemptNumber}`,
      fr: `Nouvelle tentative d'enregistrement ${attemptNumber}`,
    }
    return messages[locale] || messages.en
  }) as ErrorMessageWithParamFunction,

  unknownError: ((locale: SupportedLocales = 'en') => {
    const messages = {
      nl: 'Onbekende fout',
      en: 'Unknown error',
      de: 'Unbekannter Fehler',
      fr: 'Erreur inconnue',
    }
    return messages[locale] || messages.en
  }) as ErrorMessageFunction,
}

// Export error messages with default locale (English)
// In the future, we could enhance this to accept locale from the React layer
export const getErrorMessages = (locale: SupportedLocales = 'en') => ({
  elementNotFound: (param: string) => errorMessageDefinitions.elementNotFound(param, locale),
  failedToRemoveElement: () => errorMessageDefinitions.failedToRemoveElement(locale),
  failedToUpdateElement: () => errorMessageDefinitions.failedToUpdateElement(locale),
  failedToAddElement: () => errorMessageDefinitions.failedToAddElement(locale),
  invalidPosition: () => errorMessageDefinitions.invalidPosition(locale),
  invalidDimensions: () => errorMessageDefinitions.invalidDimensions(locale),
  failedToGetUploadUrl: () => errorMessageDefinitions.failedToGetUploadUrl(locale),
  failedToUploadImage: () => errorMessageDefinitions.failedToUploadImage(locale),
  failedToCreateDesign: () => errorMessageDefinitions.failedToCreateDesign(locale),
  failedToLoadDesign: () => errorMessageDefinitions.failedToLoadDesign(locale),
  retryingSaveAttempt: (attemptNumber: string) =>
    errorMessageDefinitions.retryingSaveAttempt(attemptNumber, locale),
  unknownError: () => errorMessageDefinitions.unknownError(locale),
})

// Default export for convenience (uses English)
export const errorMessages = getErrorMessages('en')
